// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { xBaseTheme } from "@bg/XDL/types";
import { autoClamp } from "@bg/XDL/Utilities";
import { bgsFontConfig } from "@bgs/XDL/PreStyle/typography";
import { bpFontConfig } from "@bp/XDL/PreStyle/typography";
import { getProp } from "@insite/mobius/utilities";
import { css } from "styled-components";

export const bgFontConfig = {
    url: "https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700&display=swap",
    weight: {
        light: 300,
        regular: 400,
        semiBold: 500,
        bold: 700,
    },
    fontSize: {
        xxSmall: 12,
        xSmall: 13,
        small: 14,
        regular: 15,
        large: 18,
        legend: 10,
    },
    lineHeight: {
        xSmall: "21px",
        small: "22px",
        regular: "23px",
        singleLine: "18px",
        singleLineSmall: "16px",
        singleLinexSmall: "15px",
        singleLinexXSmall: "14px",
        legend: "12px",
    },
    spacing: {
        xSmall: "13px",
        small: "14px",
        regular: "15px",
        heading: "20px",
        smallHeading: "16px",
        xSmallHeading: "12px",
    },
    fontFamily: "Roboto, sans-serif",
};

export default {
    fontFamilyImportUrl: "",
    body: {
        size: "15px",
        lineHeight: "23px",
        weight: bgFontConfig.weight.regular,
        fontFamily: bgFontConfig.fontFamily,
        css: css`
            transition: all ${getProp("theme.transition.duration.regular")}ms ease-in-out;
        `,
    },
    p: {
        size: "15px",
        lineHeight: "23px",
        weight: bgFontConfig.weight.regular,
        fontFamily: bgFontConfig.fontFamily,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.small};
            display: block;
        `,
    },
    h1: {
        size: autoClamp(38, 38, 46),
        lineHeight: autoClamp(44, 44, 54),
        weight: bgFontConfig.weight.bold,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.heading};
            display: block;
        `,
    },
    h2: {
        size: autoClamp(32, 32, 36),
        lineHeight: autoClamp(38, 38, 42),
        weight: bgFontConfig.weight.bold,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.smallHeading};
            display: block;
        `,
    },
    h3: {
        size: autoClamp(30, 30, 32),
        lineHeight: autoClamp(35, 35, 38),
        weight: bgFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.smallHeading};
            display: block;
        `,
    },
    h4: {
        size: 28,
        lineHeight: "33px",
        weight: bgFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    h5: {
        size: 24,
        lineHeight: "28px",
        weight: bgFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    h6: {
        size: 18,
        lineHeight: bgFontConfig.lineHeight.xSmall,
        weight: bgFontConfig.weight.semiBold,
        css: css`
            margin-bottom: ${bgFontConfig.spacing.xSmallHeading};
            display: block;
        `,
    },
    legend: {
        forwardAs: "span",
        size: bgFontConfig.fontSize.legend,
        lineHeight: bgFontConfig.lineHeight.legend,
        weight: bgFontConfig.weight.semiBold,
    },
    headerPrimary: {
        forwardAs: "p",
        size: "24px",
        fontFamily: bgFontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
    headerSecondary: {
        forwardAs: "p",
        size: "15px",
        weight: bgFontConfig.weight.bold,
        fontFamily: bgFontConfig.fontFamily,
        transform: "uppercase",
    },
    headerTertiary: {
        forwardAs: "p",
        size: "15px",
        fontFamily: bgFontConfig.fontFamily,
        color: "text.main",
        transform: "uppercase",
    },
} as xBaseTheme["typography"];

export const fontConfig =
    BLUEPRINT_NAME === "bp" ? bpFontConfig : BLUEPRINT_NAME === "bgs" ? bgsFontConfig : bgFontConfig;
